import { useEffect, useState } from "react";
import { toast, Toaster } from "sonner";
import { adminLogin, adminGetContent, adminSaveContent, adminGetLeads, adminChangePassword, setToken, clearToken, getToken } from "../lib/api";
import { HeartLogo } from "../components/Icons";
import { Field, Card, ObjectListEditor, StringListEditor } from "../components/AdminFields";
import { applyTheme, DEFAULT_THEME } from "../lib/theme";
import { Logo } from "../components/Logo";

export default function Admin() {
  const [authed, setAuthed] = useState(!!getToken());
  const [tab, setTab] = useState("stats");
  const [content, setContent] = useState(null);
  const [stats, setStats] = useState({ leads: 0, clicks_total: 0, clicks_ios: 0, clicks_android: 0, clicks_default: 0 });
  const [leads, setLeads] = useState([]);

  const load = async () => {
    try {
      const data = await adminGetContent();
      setContent(data.content);
      setStats(data.stats);
      applyTheme(data.content?.theme || DEFAULT_THEME);
    } catch (e) {
      if (e.response?.status === 401) { clearToken(); setAuthed(false); }
    }
  };

  useEffect(() => { if (authed) load(); }, [authed]);
  useEffect(() => {
    if (authed && tab === "leads") {
      adminGetLeads(200).then((d) => setLeads(d.leads || [])).catch(() => {});
    }
  }, [authed, tab]);

  if (!authed) return <Login onSuccess={() => setAuthed(true)} />;
  if (!content) return <div className="min-h-screen flex items-center justify-center grad-text font-head text-xl">Загрузка…</div>;

  const set = (path, value) => {
    setContent((prev) => {
      const next = structuredClone(prev);
      let obj = next;
      for (let i = 0; i < path.length - 1; i++) obj = obj[path[i]];
      obj[path[path.length - 1]] = value;
      return next;
    });
  };

  const save = async () => {
    try {
      await adminSaveContent(content);
      toast.success("Сохранено");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Ошибка сохранения");
    }
  };

  const logout = () => { clearToken(); setAuthed(false); };

  const tabs = [
    { id: "stats", label: "Ссылка и статистика" },
    { id: "content", label: "Контент" },
    { id: "appearance", label: "Оформление" },
    { id: "leads", label: "Лиды" },
    { id: "security", label: "Безопасность" },
  ];

  return (
    <div className="min-h-screen">
      <Toaster theme="dark" position="top-center" />
      <header className="sticky top-0 z-40 border-b" style={{ background: "color-mix(in srgb, var(--bg) 82%, transparent)", backdropFilter: "blur(14px)", borderColor: "var(--glass-border)" }}>
        <div className="max-w-5xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2"><HeartLogo size={22} /><span className="font-head font-bold">{content.theme?.brand_name || "CosmicLove"} · Админ</span></div>
          <div className="flex items-center gap-2">
            {(tab === "content" || tab === "appearance") && <button className="btn-grad px-5 py-2 text-sm" onClick={save} data-testid="admin-save-btn">Сохранить</button>}
            <button className="btn-ghost px-4 py-2 text-sm" onClick={logout} data-testid="admin-logout-btn">Выход</button>
          </div>
        </div>
        <div className="max-w-5xl mx-auto px-4 flex gap-1 overflow-x-auto">
          {tabs.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} className="px-3 py-2.5 text-sm whitespace-nowrap"
              style={{ color: tab === t.id ? "var(--text)" : "var(--text-sec)", borderBottom: tab === t.id ? "2px solid #F472B6" : "2px solid transparent" }}
              data-testid={`admin-tab-${t.id}`}>
              {t.label}
            </button>
          ))}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6">
        {tab === "stats" && (
          <div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
              <div className="glass p-5"><div className="text-xs uppercase tracking-wider mb-1" style={{ color: "var(--text-sec)" }}>Всего лидов</div><div className="font-head text-3xl font-bold" style={{ color: "var(--gold)" }} data-testid="stat-leads">{stats.leads}</div></div>
              <div className="glass p-5"><div className="text-xs uppercase tracking-wider mb-1" style={{ color: "var(--text-sec)" }}>Переходов всего</div><div className="font-head text-3xl font-bold" style={{ color: "var(--gold)" }} data-testid="stat-clicks-total">{stats.clicks_total}</div></div>
              <div className="glass p-5"><div className="text-xs uppercase tracking-wider mb-1" style={{ color: "var(--text-sec)" }}>iOS</div><div className="font-head text-3xl font-bold" style={{ color: "var(--gold)" }} data-testid="stat-clicks-ios">{stats.clicks_ios}</div></div>
              <div className="glass p-5"><div className="text-xs uppercase tracking-wider mb-1" style={{ color: "var(--text-sec)" }}>Android</div><div className="font-head text-3xl font-bold" style={{ color: "var(--gold)" }} data-testid="stat-clicks-android">{stats.clicks_android}</div></div>
              <div className="glass p-5"><div className="text-xs uppercase tracking-wider mb-1" style={{ color: "var(--text-sec)" }}>Прочее</div><div className="font-head text-3xl font-bold" style={{ color: "var(--gold)" }} data-testid="stat-clicks-default">{stats.clicks_default}</div></div>
            </div>
            <Card title="Ссылки на скачивание">
              <Field label="Ссылка для iOS (App Store)" value={content.redirect_ios} onChange={(v) => set(["redirect_ios"], v)} placeholder="https://apps.apple.com/..." testid="admin-redirect-ios" />
              <Field label="Ссылка для Android (Google Play)" value={content.redirect_android} onChange={(v) => set(["redirect_android"], v)} placeholder="https://play.google.com/..." testid="admin-redirect-android" />
              <Field label="Ссылка по умолчанию (десктоп/прочее)" value={content.redirect_default} onChange={(v) => set(["redirect_default"], v)} placeholder="https://..." testid="admin-redirect-default" />
              <button className="btn-grad px-5 py-2 text-sm" onClick={save} data-testid="admin-save-redirect">Сохранить</button>
            </Card>
          </div>
        )}

        {tab === "content" && <ContentEditor content={content} set={set} />}

        {tab === "appearance" && <AppearanceEditor content={content} set={set} save={save} />}

        {tab === "leads" && (
          <div className="glass overflow-x-auto" style={{ padding: "1rem" }}>
            <table className="w-full text-sm" data-testid="leads-table">
              <thead><tr style={{ color: "var(--text-sec)" }} className="text-left">
                <th className="py-2 pr-4">Пол</th><th className="py-2 pr-4">Возраст</th><th className="py-2 pr-4">Цель</th><th className="py-2 pr-4">Интересы</th><th className="py-2">Дата</th>
              </tr></thead>
              <tbody>
                {leads.length === 0 && <tr><td colSpan={5} className="py-6 text-center" style={{ color: "var(--text-sec)" }}>Пока нет лидов</td></tr>}
                {leads.map((l, i) => (
                  <tr key={i} style={{ borderTop: "1px solid var(--glass-border)" }}>
                    <td className="py-2 pr-4">{l.gender || "—"}</td>
                    <td className="py-2 pr-4">{l.age || "—"}</td>
                    <td className="py-2 pr-4">{l.goal || "—"}</td>
                    <td className="py-2 pr-4">{(l.interests || []).join(", ") || "—"}</td>
                    <td className="py-2" style={{ color: "var(--text-sec)" }}>{l.created_at ? new Date(l.created_at).toLocaleString("ru-RU") : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "security" && <Security onLoggedOut={logout} />}
      </main>
    </div>
  );
}

function ColorField({ label, value, onChange, testid }) {
  const hex = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(value || "");
  return (
    <label className="block mb-3">
      <span className="block text-xs mb-1 uppercase tracking-wider" style={{ color: "var(--text-sec)" }}>{label}</span>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={hex ? value : "#000000"}
          onChange={(e) => onChange(e.target.value)}
          style={{ width: 42, height: 40, borderRadius: 10, border: "1px solid var(--glass-border)", background: "transparent", cursor: "pointer", padding: 2 }}
          data-testid={testid ? `${testid}-picker` : undefined}
        />
        <input className="a-input flex-1" value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
      </div>
    </label>
  );
}

function ThemePreview({ theme }) {
  const c = theme.colors || {};
  const gradient = `linear-gradient(135deg, ${c.accent_from} 0%, ${c.accent_to} 100%)`;
  const styleVars = {
    background: c.bg,
    borderRadius: theme.card_radius || "20px",
    border: `1px solid ${c.card_border}`,
    padding: "1.4rem",
    color: c.text_primary,
    position: "relative",
    overflow: "hidden",
  };
  return (
    <div style={styleVars} data-testid="theme-preview">
      <div style={{ position: "absolute", top: "-40%", left: "10%", width: "70%", height: "120%", background: `radial-gradient(circle, ${c.bg_glow} 0%, transparent 70%)`, filter: "blur(20px)", pointerEvents: "none" }} />
      <div style={{ position: "relative" }}>
        <div className="flex items-center gap-2 mb-3">
          <Logo theme={theme} size={26} />
          {theme.logo_type !== "text" && <span style={{ fontFamily: `'${theme.fonts?.heading}', sans-serif`, fontWeight: 700 }}>{theme.brand_name}</span>}
        </div>
        <div style={{ fontFamily: `'${theme.fonts?.heading}', sans-serif`, fontWeight: 800, fontSize: "1.5rem", lineHeight: 1.1, marginBottom: 8 }}>
          Притяжение начинается здесь
        </div>
        <div style={{ color: c.text_secondary, fontSize: "0.85rem", marginBottom: 14, fontFamily: `'${theme.fonts?.body}', sans-serif` }}>
          Пример подзаголовка в выбранной теме.
        </div>
        <div className="flex items-center gap-3">
          <button style={{ background: gradient, color: "#fff", border: "none", borderRadius: theme.button_radius || "999px", padding: "0.6rem 1.2rem", fontWeight: 700, cursor: "default", boxShadow: `0 0 20px ${c.accent_to}66` }}>
            Кнопка
          </button>
          <span style={{ background: gradient, color: "#fff", borderRadius: "999px", padding: "0.3rem 0.8rem", fontSize: "0.75rem", fontWeight: 700 }}>92%</span>
          <span style={{ color: c.accent_secondary, fontWeight: 800, fontFamily: `'${theme.fonts?.heading}', sans-serif` }}>180k+</span>
        </div>
      </div>
    </div>
  );
}

function AppearanceEditor({ content, set, save }) {
  const theme = content.theme || DEFAULT_THEME;
  const c = theme.colors || {};
  const setC = (key, v) => set(["theme", "colors", key], v);

  const preview = () => {
    try { localStorage.setItem("cosmic_preview_theme", JSON.stringify(theme)); } catch (e) {}
    window.open("/?preview=1", "_blank");
  };
  const reset = () => {
    set(["theme"], structuredClone(DEFAULT_THEME));
    applyTheme(DEFAULT_THEME);
    toast.message("Тема сброшена к дефолту. Не забудьте «Сохранить».");
  };

  // live-apply to admin as user edits
  useEffect(() => { applyTheme(theme); }, [theme]);

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div>
        <Card title="Бренд и логотип">
          <Field label="Название бренда" value={theme.brand_name} onChange={(v) => set(["theme", "brand_name"], v)} testid="theme-brand-name" />
          <label className="block mb-3">
            <span className="block text-xs mb-1 uppercase tracking-wider" style={{ color: "var(--text-sec)" }}>Тип логотипа</span>
            <select className="a-input" value={theme.logo_type} onChange={(e) => set(["theme", "logo_type"], e.target.value)} data-testid="theme-logo-type">
              <option value="emoji">Эмодзи</option>
              <option value="text">Текст</option>
              <option value="svg">SVG (инлайн)</option>
            </select>
          </label>
          <Field label={theme.logo_type === "svg" ? "Инлайн-SVG код" : theme.logo_type === "text" ? "Текст логотипа" : "Эмодзи"} textarea={theme.logo_type === "svg"} value={theme.logo_value} onChange={(v) => set(["theme", "logo_value"], v)} testid="theme-logo-value" />
        </Card>

        <Card title="Цвета">
          <ColorField label="Фон страницы" value={c.bg} onChange={(v) => setC("bg", v)} testid="theme-color-bg" />
          <ColorField label="Свечение hero" value={c.bg_glow} onChange={(v) => setC("bg_glow", v)} testid="theme-color-bg-glow" />
          <ColorField label="Акцент (старт)" value={c.accent_from} onChange={(v) => setC("accent_from", v)} testid="theme-color-accent-from" />
          <ColorField label="Акцент (конец)" value={c.accent_to} onChange={(v) => setC("accent_to", v)} testid="theme-color-accent-to" />
          <ColorField label="Вторичный акцент" value={c.accent_secondary} onChange={(v) => setC("accent_secondary", v)} testid="theme-color-accent-secondary" />
          <ColorField label="Текст основной" value={c.text_primary} onChange={(v) => setC("text_primary", v)} testid="theme-color-text-primary" />
          <ColorField label="Текст вторичный" value={c.text_secondary} onChange={(v) => setC("text_secondary", v)} testid="theme-color-text-secondary" />
          <Field label="Фон карточек (rgba)" value={c.card_bg} onChange={(v) => setC("card_bg", v)} testid="theme-color-card-bg" />
          <Field label="Граница карточек (rgba)" value={c.card_border} onChange={(v) => setC("card_border", v)} testid="theme-color-card-border" />
        </Card>

        <Card title="Шрифты">
          <Field label="Заголовки (имя с Google Fonts)" value={theme.fonts?.heading} onChange={(v) => set(["theme", "fonts", "heading"], v)} testid="theme-font-heading" />
          <Field label="Текст (имя с Google Fonts)" value={theme.fonts?.body} onChange={(v) => set(["theme", "fonts", "body"], v)} testid="theme-font-body" />
          <p className="text-xs" style={{ color: "var(--text-sec)" }}>Имя должно точно совпадать с названием на fonts.google.com (например: Unbounded, Manrope, Montserrat).</p>
        </Card>

        <Card title="Фон и формы">
          <label className="block mb-3">
            <span className="block text-xs mb-1 uppercase tracking-wider" style={{ color: "var(--text-sec)" }}>Стиль фона</span>
            <select className="a-input" value={theme.background_style} onChange={(e) => set(["theme", "background_style"], e.target.value)} data-testid="theme-bg-style">
              <option value="stars">Звёздное небо</option>
              <option value="gradient">Градиент</option>
              <option value="plain">Однотонный</option>
            </select>
          </label>
          <Field label="Радиус кнопок" value={theme.button_radius} onChange={(v) => set(["theme", "button_radius"], v)} testid="theme-btn-radius" />
          <Field label="Радиус карточек" value={theme.card_radius} onChange={(v) => set(["theme", "card_radius"], v)} testid="theme-card-radius" />
        </Card>
      </div>

      <div className="lg:sticky lg:top-28 self-start">
        <div className="text-xs uppercase tracking-wider mb-2" style={{ color: "var(--text-sec)" }}>Живой предпросмотр</div>
        <ThemePreview theme={theme} />
        <div className="flex flex-wrap gap-2 mt-4">
          <button className="btn-grad px-5 py-2.5 text-sm" onClick={save} data-testid="theme-save-btn">Сохранить</button>
          <button className="btn-ghost px-5 py-2.5 text-sm" onClick={preview} data-testid="theme-preview-btn">Предпросмотр на сайте</button>
          <button className="btn-ghost px-5 py-2.5 text-sm" onClick={reset} data-testid="theme-reset-btn">Сбросить к дефолту</button>
        </div>
      </div>
    </div>
  );
}


function ContentEditor({ content, set }) {
  return (
    <div>
      <Card title="Бренд">
        <Field label="Название" value={content.brand?.name} onChange={(v) => set(["brand", "name"], v)} />
        <Field label="Тэглайн" value={content.brand?.tagline} onChange={(v) => set(["brand", "tagline"], v)} />
      </Card>

      <Card title="Хедер">
        <Field label="Текст CTA" value={content.header?.cta_text} onChange={(v) => set(["header", "cta_text"], v)} />
        <div className="text-xs mb-2 mt-3 uppercase tracking-wider" style={{ color: "var(--text-sec)" }}>Навигация</div>
        <ObjectListEditor items={content.header?.nav || []} onChange={(v) => set(["header", "nav"], v)} labelPrefix="Ссылка"
          addTemplate={{ label: "Раздел", anchor: "#about" }} fields={[{ key: "label", label: "Название" }, { key: "anchor", label: "Якорь (#id)" }]} />
      </Card>

      <Card title="Hero">
        <Field label="Надзаголовок" value={content.hero?.eyebrow} onChange={(v) => set(["hero", "eyebrow"], v)} />
        <Field label="Заголовок" value={content.hero?.title} onChange={(v) => set(["hero", "title"], v)} />
        <Field label="Подзаголовок" textarea value={content.hero?.subtitle} onChange={(v) => set(["hero", "subtitle"], v)} />
        <Field label="Кнопка 1" value={content.hero?.cta_primary} onChange={(v) => set(["hero", "cta_primary"], v)} />
        <Field label="Кнопка 2" value={content.hero?.cta_secondary} onChange={(v) => set(["hero", "cta_secondary"], v)} />
        <Field label="Бейдж мэтча" value={content.hero?.match_badge} onChange={(v) => set(["hero", "match_badge"], v)} />
        <Field label="Имя в карточке" value={content.hero?.match_name} onChange={(v) => set(["hero", "match_name"], v)} />
        <Field label="Подпись карточки" value={content.hero?.match_caption} onChange={(v) => set(["hero", "match_caption"], v)} />
      </Card>

      <Card title="Статистика">
        <ObjectListEditor items={content.stats || []} onChange={(v) => set(["stats"], v)} labelPrefix="Цифра"
          addTemplate={{ value: "0", label: "" }} fields={[{ key: "value", label: "Значение" }, { key: "label", label: "Подпись" }]} />
      </Card>

      <Card title="Фичи «Почему CosmicLove»">
        <Field label="Заголовок секции" value={content.features_title} onChange={(v) => set(["features_title"], v)} />
        <Field label="Подзаголовок" value={content.features_subtitle} onChange={(v) => set(["features_subtitle"], v)} />
        <ObjectListEditor items={content.features || []} onChange={(v) => set(["features"], v)} labelPrefix="Фича"
          addTemplate={{ icon: "sparkles", title: "", text: "" }}
          fields={[{ key: "icon", label: "Иконка (heart-handshake/shield-check/sparkles/gift/heart/users/compass)" }, { key: "title", label: "Заголовок" }, { key: "text", label: "Текст", textarea: true }]} />
      </Card>

      <Card title="Кому подходит">
        <Field label="Заголовок секции" value={content.audience_title} onChange={(v) => set(["audience_title"], v)} />
        <Field label="Подзаголовок" value={content.audience_subtitle} onChange={(v) => set(["audience_subtitle"], v)} />
        <ObjectListEditor items={content.audience || []} onChange={(v) => set(["audience"], v)} labelPrefix="Карточка"
          addTemplate={{ title: "", text: "" }} fields={[{ key: "title", label: "Заголовок" }, { key: "text", label: "Текст", textarea: true }]} />
      </Card>

      <Card title="Квиз">
        <Field label="Заголовок" value={content.quiz?.title} onChange={(v) => set(["quiz", "title"], v)} />
        <Field label="Подзаголовок" value={content.quiz?.subtitle} onChange={(v) => set(["quiz", "subtitle"], v)} />
        <Field label="Текст «Назад»" value={content.quiz?.back_text} onChange={(v) => set(["quiz", "back_text"], v)} />
        <Field label="Прогресс (используйте {x} и {n})" value={content.quiz?.progress_text} onChange={(v) => set(["quiz", "progress_text"], v)} />
        <div className="text-xs mb-2 mt-4 uppercase tracking-wider" style={{ color: "var(--text-sec)" }}>Шаги</div>
        {(content.quiz?.steps || []).map((st, i) => (
          <details key={i} className="mb-3" style={{ border: "1px solid var(--glass-border)", borderRadius: 12, padding: "0.8rem 1rem" }}>
            <summary className="cursor-pointer font-semibold text-sm">Шаг {i + 1}: {st.question} <span style={{ color: "var(--text-sec)" }}>({st.type})</span></summary>
            <div className="mt-3">
              <Field label="Вопрос" value={st.question} onChange={(v) => set(["quiz", "steps", i, "question"], v)} />
              {st.type === "range" && (
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Мин" value={st.min} onChange={(v) => set(["quiz", "steps", i, "min"], Number(v) || 0)} />
                  <Field label="Макс" value={st.max} onChange={(v) => set(["quiz", "steps", i, "max"], Number(v) || 0)} />
                </div>
              )}
              {st.type === "multi" && <Field label="Текст кнопки «Далее»" value={st.next_text} onChange={(v) => set(["quiz", "steps", i, "next_text"], v)} />}
              {(st.type === "single" || st.type === "multi") && (
                <>
                  <div className="text-xs mb-2 mt-2 uppercase tracking-wider" style={{ color: "var(--text-sec)" }}>Варианты</div>
                  <StringListEditor items={st.options || []} onChange={(v) => set(["quiz", "steps", i, "options"], v)} />
                </>
              )}
            </div>
          </details>
        ))}
      </Card>

      <Card title="Финальный экран">
        <Field label="Заголовок" value={content.final?.title} onChange={(v) => set(["final", "title"], v)} />
        <Field label="Подзаголовок" value={content.final?.subtitle} onChange={(v) => set(["final", "subtitle"], v)} />
        <Field label="Кнопка скачивания" value={content.final?.download_button} onChange={(v) => set(["final", "download_button"], v)} />
      </Card>

      <Card title="Модалка редиректа">
        <Field label="Заголовок" value={content.modal?.title} onChange={(v) => set(["modal", "title"], v)} />
        <Field label="Текст" textarea value={content.modal?.text} onChange={(v) => set(["modal", "text"], v)} />
        <Field label="Кнопка подтверждения" value={content.modal?.confirm_text} onChange={(v) => set(["modal", "confirm_text"], v)} />
        <Field label="Кнопка отмены" value={content.modal?.cancel_text} onChange={(v) => set(["modal", "cancel_text"], v)} />
      </Card>

      {["privacy", "terms", "support"].map((k) => (
        <Card key={k} title={`Легал · ${k === "privacy" ? "Конфиденциальность" : k === "terms" ? "Условия" : "Поддержка"}`}>
          <Field label="Заголовок" value={content.legal?.[k]?.title} onChange={(v) => set(["legal", k, "title"], v)} />
          <Field label="Текст" textarea value={content.legal?.[k]?.body} onChange={(v) => set(["legal", k, "body"], v)} />
        </Card>
      ))}

      <Card title="Возрастной гейт (18+)">
        <Field label="Заголовок" value={content.age_gate?.title} onChange={(v) => set(["age_gate", "title"], v)} />
        <Field label="Текст" textarea value={content.age_gate?.text} onChange={(v) => set(["age_gate", "text"], v)} />
        <Field label="Кнопка «Есть 18»" value={content.age_gate?.confirm_text} onChange={(v) => set(["age_gate", "confirm_text"], v)} />
        <Field label="Кнопка «Нет 18»" value={content.age_gate?.decline_text} onChange={(v) => set(["age_gate", "decline_text"], v)} />
        <Field label="Текст блокировки" textarea value={content.age_gate?.blocked_text} onChange={(v) => set(["age_gate", "blocked_text"], v)} />
        <Field label="Редирект при «Нет 18»" value={content.age_gate?.decline_redirect_url} onChange={(v) => set(["age_gate", "decline_redirect_url"], v)} />
      </Card>

      <Card title="Cookie-consent">
        <Field label="Текст" textarea value={content.cookie_consent?.text} onChange={(v) => set(["cookie_consent", "text"], v)} />
        <Field label="Кнопка «Принять»" value={content.cookie_consent?.accept_text} onChange={(v) => set(["cookie_consent", "accept_text"], v)} />
      </Card>

      <Card title="Футер">
        <Field label="Тэглайн" value={content.footer?.tagline} onChange={(v) => set(["footer", "tagline"], v)} />
        <Field label="Копирайт" value={content.footer?.copyright} onChange={(v) => set(["footer", "copyright"], v)} />
      </Card>
    </div>
  );
}

function Login({ onSuccess }) {
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setErr("");
    try {
      const { token } = await adminLogin(pw);
      setToken(token);
      onSuccess();
    } catch (e) {
      setErr(e.response?.data?.detail || "Ошибка входа");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-5">
      <form onSubmit={submit} className="glass w-full max-w-sm" style={{ padding: "2.2rem" }} data-testid="admin-login-form">
        <div className="flex items-center gap-2 justify-center mb-6"><HeartLogo /><span className="font-head text-xl font-bold">CosmicLove · Админ</span></div>
        <input className="a-input mb-3" type="password" placeholder="Пароль" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="admin-password-input" autoFocus />
        {err && <p className="text-sm mb-3" style={{ color: "#EF4444" }} data-testid="admin-login-error">{err}</p>}
        <button className="btn-grad w-full py-3" disabled={loading} data-testid="admin-login-btn">{loading ? "Вход…" : "Войти"}</button>
      </form>
    </div>
  );
}

function Security({ onLoggedOut }) {
  const [oldP, setOldP] = useState("");
  const [newP, setNewP] = useState("");
  const [rep, setRep] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (newP.length < 8) return toast.error("Новый пароль должен быть не менее 8 символов");
    if (newP !== rep) return toast.error("Пароли не совпадают");
    try {
      await adminChangePassword(oldP, newP);
      toast.success("Пароль изменён. Войдите заново.");
      setTimeout(onLoggedOut, 1200);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Ошибка смены пароля");
    }
  };

  return (
    <form onSubmit={submit} className="glass max-w-md" style={{ padding: "1.6rem" }} data-testid="security-form">
      <h3 className="font-head font-bold text-lg mb-4">Смена пароля</h3>
      <input className="a-input mb-3" type="password" placeholder="Старый пароль" value={oldP} onChange={(e) => setOldP(e.target.value)} data-testid="old-password-input" />
      <input className="a-input mb-3" type="password" placeholder="Новый пароль (мин. 8)" value={newP} onChange={(e) => setNewP(e.target.value)} data-testid="new-password-input" />
      <input className="a-input mb-4" type="password" placeholder="Повторите новый пароль" value={rep} onChange={(e) => setRep(e.target.value)} data-testid="repeat-password-input" />
      <button className="btn-grad px-6 py-2.5" data-testid="change-password-btn">Сменить пароль</button>
    </form>
  );
}
